import Vue from 'vue';

export type MentionCategory =
  | 'Bolagsinformation'
  | 'Resultatpost'
  | 'Balanspost'
  | 'Kassaflödesterm'
  | 'Nyckeltal';

export type MentionPeriod = 'senaste' | 'foregaende' | 'forandring';

export type FormatKind =
  | 'amount'
  | 'percent'
  | 'percent0'
  | 'percentPoints'
  | 'multiple'
  | 'text';

export interface MentionDef {
  /** Stable token id, e.g. pl-nettoomsattning-prev */
  id: string;
  /** Base id without period suffix */
  baseId: string;
  name: string;
  category: MentionCategory;
  period: MentionPeriod;
  format: FormatKind;
  /** Resolvable for saved slides, omitted from the mention picker */
  hidden?: boolean;
  /** Raw numeric value for sheet index (0 = latest, 1 = previous) */
  getValue: (data: any, sheetIndex: number) => number | null;
  /** Text value for Bolagsinformation (and other non-numeric tags) */
  getText?: (data: any) => string | null;
}

export interface MentionItem {
  id: string;
  name: string;
  category: MentionCategory;
  period: MentionPeriod;
  formattedValue: string;
}

const TOKEN_RE = /\{\{([a-z0-9-]+)\}\}/g;

const PERIOD_LABEL: Record<MentionPeriod, string> = {
  senaste: '',
  foregaende: ' (föregående år)',
  forandring: ' (förändring)',
};

function filters(): any {
  return (Vue as any).options.filters || {};
}

function num(v: any): number | null {
  if (v === null || v === undefined || v === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function sheet(data: any, kind: 'profitloss' | 'balance' | 'cashflow', i: number): any | null {
  const sheets = data && data[kind] && data[kind].sheets;
  if (!sheets || !sheets[i]) {
    return null;
  }
  return sheets[i];
}

function plField(field: string) {
  return (data: any, i: number) => {
    const s = sheet(data, 'profitloss', i);
    return s ? num(s[field]) : null;
  };
}

function bsField(field: string) {
  return (data: any, i: number) => {
    const s = sheet(data, 'balance', i);
    return s ? num(s[field]) : null;
  };
}

function cfField(field: string) {
  return (data: any, i: number) => {
    const s = sheet(data, 'cashflow', i);
    return s ? num(s[field]) : null;
  };
}

function div(a: number | null, b: number | null): number | null {
  if (a === null || b === null || b === 0) {
    return null;
  }
  return a / b;
}

function employees(data: any): number | null {
  const raw = data && data.overview && data.overview.NR_EMPLOYEES;
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const n = Number(String(raw).replace(/\s/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function employeeLine(data: any, one: string, many: string): string | null {
  const raw = data && data.overview && data.overview.NR_EMPLOYEES;
  if (raw === null || raw === undefined || String(raw).trim() === '' || String(raw).trim() === '-') {
    return null;
  }
  const n = Number(String(raw).replace(/\s/g, ''));
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  const phrase = n === 1 ? `1 ${one}` : `${applyFilter('D0', String(n))} ${many}`;
  const dateTo = overviewField('DATE_TO')(data);
  if (!dateTo || dateTo.length < 4) {
    return phrase;
  }
  return `${phrase} (${dateTo.substring(0, 4)})`;
}

interface BaseMention {
  baseId: string;
  name: string;
  category: MentionCategory;
  format: FormatKind;
  getValue: (data: any, sheetIndex: number) => number | null;
  getText?: (data: any) => string | null;
  /** If true, only generate latest period */
  latestOnly?: boolean;
  /** Resolvable for saved slides, omitted from the mention picker */
  hidden?: boolean;
}

function amt(
  baseId: string,
  name: string,
  category: MentionCategory,
  getValue: (data: any, sheetIndex: number) => number | null,
): BaseMention {
  return { baseId, name, category, format: 'amount', getValue };
}

function hiddenAmt(
  baseId: string,
  name: string,
  getValue: (data: any, sheetIndex: number) => number | null,
): BaseMention {
  return { ...amt(baseId, name, 'Kassaflödesterm', getValue), hidden: true };
}

/** Short-term credit-institution debt (U) plus all long-term liabilities (P). */
function interestBearingLiabilities(data: any, i: number): number | null {
  const s = sheet(data, 'balance', i);
  if (!s) {
    return null;
  }
  const u = num(s.U);
  const p = num(s.P);
  if (u === null && p === null) {
    return null;
  }
  return (u || 0) + (p || 0);
}

function quickRatio(data: any, i: number): number | null {
  const s = sheet(data, 'balance', i);
  if (!s) {
    return null;
  }
  return div((num(s.K) || 0) - (num(s.F) || 0), num(s.Q));
}

function solidity(data: any, i: number): number | null {
  const s = sheet(data, 'balance', i);
  if (!s) {
    return null;
  }
  const m = num(s.M);
  const n = num(s.N);
  const l = num(s.L);
  if (m === null || l === null || l === 0) {
    return null;
  }
  return (m + 0.794 * (n || 0)) / l;
}

function incorporationYear(data: any): string | null {
  const raw = overviewField('INCORPORATION_DATE')(data);
  if (!raw || raw.length < 4) {
    return null;
  }
  return raw.substr(0, 4);
}

function overviewField(field: string): (data: any) => string | null {
  return (data: any): string | null => {
    const o = data && data.overview;
    if (!o) {
      return null;
    }
    const v = o[field];
    if (v === null || v === undefined) {
      return null;
    }
    const s = String(v).trim();
    if (!s || s === '-') {
      return null;
    }
    return s;
  };
}

function applyFilter(name: string, value: string): string {
  const f = filters();
  if (f[name]) {
    return f[name](value);
  }
  return value;
}

function info(
  baseId: string,
  name: string,
  getText: (data: any) => string | null,
): BaseMention {
  return {
    baseId,
    name,
    category: 'Bolagsinformation',
    format: 'text',
    getValue: () => null,
    getText,
    latestOnly: true,
  };
}

const BASE_MENTIONS: BaseMention[] = [
  // --- Resultatposter ---
  amt('pl-nettoomsattning', 'Nettoomsättning', 'Resultatpost', plField('A')),
  amt('pl-ovriga-rorelseintakter', 'Övriga rörelseintäkter', 'Resultatpost', plField('B')),
  amt('pl-summa-intakter', 'Summa intäkter', 'Resultatpost', plField('C')),
  amt('pl-kostnader-varor', 'Kostnader för varor och material', 'Resultatpost', plField('D')),
  amt('pl-bruttoresultat', 'Bruttoresultat', 'Resultatpost', plField('E')),
  amt('pl-ovriga-externa', 'Övriga externa kostnader', 'Resultatpost', plField('F')),
  amt('pl-personalkostnader', 'Personalkostnader', 'Resultatpost', plField('G')),
  amt('pl-ovriga-rorelsekostnader', 'Övriga rörelsekostnader', 'Resultatpost', plField('H')),
  amt('pl-driftsresultat', 'Driftsresultat', 'Resultatpost', plField('I')),
  amt('pl-avskrivningar', 'Av- och nedskrivningar', 'Resultatpost', plField('J')),
  amt('pl-rorelseresultat', 'Rörelseresultat', 'Resultatpost', plField('K')),
  amt('pl-finansiella-intakter', 'Finansiella intäkter', 'Resultatpost', plField('L')),
  amt('pl-finansiella-kostnader', 'Finansiella kostnader', 'Resultatpost', plField('M')),
  amt('pl-finansiella-netto', 'Finansiella poster netto', 'Resultatpost', plField('LM')),
  amt('pl-efter-finansiella', 'Resultat efter finansiella poster', 'Resultatpost', plField('N')),
  amt('pl-bokslutsdispositioner', 'Bokslutsdispositioner', 'Resultatpost', plField('O')),
  amt('pl-fore-skatt', 'Resultat före skatt', 'Resultatpost', plField('P')),
  amt('pl-skatt', 'Skatt på årets resultat', 'Resultatpost', plField('Q')),
  amt('pl-arets-resultat', 'Årets resultat', 'Resultatpost', plField('R')),
  {
    baseId: 'pl-brutto-ex-personal',
    name: 'Bruttoresultat efter personalkostnader',
    category: 'Resultatpost',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      if (!s) {
        return null;
      }
      const e = num(s.E);
      const g = num(s.G);
      if (e === null || g === null) {
        return null;
      }
      return e + g;
    },
  },

  // --- Balansposter ---
  amt('bs-tecknat-kapital', 'Tecknat men ej inbetalt kapital', 'Balanspost', bsField('A')),
  amt('bs-immateriella', 'Immateriella anläggningstillgångar', 'Balanspost', bsField('B')),
  amt('bs-materiella', 'Materiella anläggningstillgångar', 'Balanspost', bsField('C')),
  amt('bs-finansiella', 'Finansiella anläggningstillgångar', 'Balanspost', bsField('D')),
  amt('bs-summa-anlaggning', 'Summa anläggningstillgångar', 'Balanspost', bsField('E')),
  amt('bs-varulager', 'Varulager m.m.', 'Balanspost', bsField('F')),
  amt('bs-kundfordringar', 'Kundfordringar', 'Balanspost', bsField('G')),
  amt('bs-ovriga-fordringar', 'Övriga kortfristiga fordringar', 'Balanspost', bsField('H')),
  amt('bs-kortfristiga-placeringar', 'Kortfristiga placeringar', 'Balanspost', bsField('I')),
  amt('bs-kassa-bank', 'Kassa och bank', 'Balanspost', bsField('J')),
  amt('bs-summa-omsattning', 'Summa omsättningstillgångar', 'Balanspost', bsField('K')),
  amt('bs-summa-tillgangar', 'Summa tillgångar', 'Balanspost', bsField('L')),
  amt('bs-eget-kapital', 'Eget kapital', 'Balanspost', bsField('M')),
  amt('bs-obeskattade', 'Obeskattade reserver', 'Balanspost', bsField('N')),
  amt('bs-avsattningar', 'Avsättningar', 'Balanspost', bsField('O')),
  amt('bs-langfristiga-skulder', 'Långfristiga skulder', 'Balanspost', bsField('P')),
  amt('bs-kortfristiga-skulder', 'Kortfristiga skulder', 'Balanspost', bsField('Q')),
  amt('bs-summa-skulder', 'Summa skulder', 'Balanspost', bsField('R')),
  amt('bs-summa-ek-skulder', 'Summa eget kapital och skulder', 'Balanspost', bsField('S')),
  {
    baseId: 'bs-rantebarande',
    name: 'Räntebärande skulder',
    category: 'Balanspost',
    format: 'amount',
    getValue: interestBearingLiabilities,
  },
  amt('bs-kreditinstitut', 'Kortfristiga skulder till kreditinstitut', 'Balanspost', bsField('U')),
  amt('bs-leverantorsskulder', 'Leverantörsskulder', 'Balanspost', bsField('V')),
  amt('bs-ovriga-kortfristiga', 'Övriga kortfristiga skulder', 'Balanspost', bsField('W')),
  {
    baseId: 'bs-justerat-eget-kapital',
    name: 'Justerat eget kapital',
    category: 'Balanspost',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'balance', i);
      if (!s) {
        return null;
      }
      const m = num(s.M);
      const n = num(s.N);
      if (m === null || n === null) {
        return null;
      }
      return m + 0.794 * n;
    },
  },
  {
    baseId: 'bs-rorelsekapital',
    name: 'Bindning i rörelsekapital (exkl. kassa och bank)',
    category: 'Balanspost',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'balance', i);
      if (!s) {
        return null;
      }
      const k = num(s.K);
      const j = num(s.J);
      const q = num(s.Q);
      const u = num(s.U);
      if (k === null || j === null || q === null || u === null) {
        return null;
      }
      return k - j - q + u;
    },
  },
  {
    baseId: 'bs-nettoskuld',
    name: 'Nettoskuld',
    category: 'Balanspost',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'balance', i);
      if (!s) {
        return null;
      }
      const d = num(s.D);
      const ii = num(s.I);
      const j = num(s.J);
      const t = num(s.T);
      if (d === null || ii === null || j === null || t === null) {
        return null;
      }
      // Same sign as valuation NET_DEBT: assets minus interest-bearing debt
      return d + ii + j - t;
    },
  },

  // --- Kassaflödestermer (excl. valuation OPERATIONAL_CASHFLOW) ---
  amt('cf-operativt', 'Kassaflöde från den operativa verksamheten', 'Kassaflödesterm', cfField('OperatingCashflow')),
  amt(
    'cf-rorelsekapital',
    'Kassaflöde från förändring av rörelsekapital',
    'Kassaflödesterm',
    cfField('WorkingCaptialCashflow'),
  ),
  {
    baseId: 'cf-lopande',
    name: 'Summa kassaflöde från den löpande verksamheten',
    category: 'Kassaflödesterm',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'cashflow', i);
      if (!s) {
        return null;
      }
      const a = num(s.OperatingCashflow);
      const b = num(s.WorkingCaptialCashflow);
      if (a === null || b === null) {
        return null;
      }
      return a + b;
    },
  },
  amt('cf-investeringar', 'Kassaflöde från investeringar', 'Kassaflödesterm', cfField('InvestmentsCashflow')),
  amt('cf-rantebarande', 'Förändring av räntebärande skulder', 'Kassaflödesterm', cfField('InterestLiabilitiesDelta')),
  {
    baseId: 'cf-insatt-agarkapital',
    name: 'Insatt eller uttaget ägarkapital',
    category: 'Kassaflödesterm',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'cashflow', i);
      if (!s) {
        return null;
      }
      const a = num(s.EquityWithoutNetProfitDelta);
      const b = num(s.SubscribedCapitalUnpaidDelta);
      if (a === null || b === null) {
        return null;
      }
      return a + b;
    },
  },
  amt('cf-finansiering', 'Kassaflöde från finansiering', 'Kassaflödesterm', cfField('FinancingCashflow')),
  amt('cf-arets', 'Årets kassaflöde', 'Kassaflödesterm', cfField('CashFlow')),
  {
    baseId: 'cf-fritt',
    name: 'Fritt kassaflöde',
    category: 'Kassaflödesterm',
    format: 'amount',
    getValue: (data, i) => {
      const s = sheet(data, 'cashflow', i);
      if (!s) {
        return null;
      }
      const a = num(s.CashFlow);
      const b = num(s.FinancingCashflow);
      if (a === null || b === null) {
        return null;
      }
      return a - b;
    },
  },
  // Kept so decks saved before the cash-flow picker was trimmed still resolve.
  hiddenAmt('cf-rorelseresultat', 'Rörelseresultat (kassaflöde)', cfField('OperatingResult')),
  hiddenAmt('cf-justering', 'Justering för ej likviditetspåverkande poster', cfField('Adjustment')),
  hiddenAmt('cf-finansiella-poster', 'Justering för finansiella poster', cfField('TotFinInvestments')),
  hiddenAmt('cf-skatt', 'Årets skatt (kassaflöde)', cfField('Tax')),
  hiddenAmt('cf-varulager', 'Förändring av varulager', cfField('InventoriesDelta')),
  hiddenAmt('cf-kundfordringar', 'Förändring av kundfordringar', cfField('AccountReceivableTradeDelta')),
  hiddenAmt('cf-ovriga-fordringar', 'Förändring av övriga kortfristiga fordringar', cfField('OtherAccReceiveDelta')),
  hiddenAmt('cf-leverantorer', 'Förändring av leverantörsskulder', cfField('AccPayTradeDelta')),
  hiddenAmt('cf-ovriga-skulder', 'Förändring av övriga kortfristiga skulder', cfField('OtherCurrentLiabilitiesDelta')),
  hiddenAmt('cf-anlaggningstillgangar', 'Förändring av anläggningstillgångar', cfField('TotFixAssetsDelta')),
  hiddenAmt('cf-avskrivningar', 'Justering för av- och nedskrivningar', cfField('DeprAndWriteDowns')),
  hiddenAmt('cf-kortfristiga-placeringar', 'Förändring av kortfristiga placeringar', cfField('ShortInvestmentsDelta')),
  hiddenAmt('cf-agarkapital', 'Uttag eller insättning av ägarkapital', cfField('EquityWithoutNetProfitDelta')),
  hiddenAmt('cf-tecknat-kapital', 'Förändring av tecknat men ej inbetalt kapital', cfField('SubscribedCapitalUnpaidDelta')),
  hiddenAmt('cf-justering-error', 'Övriga justeringar för ej likviditetspåverkande poster', cfField('CashFlowError')),
  hiddenAmt('cf-obeskattade', 'Förändring av obeskattade reserver', cfField('UntaxedReservesDelta')),
  {
    baseId: 'cf-rorelsekapital-delta',
    name: 'Förändring av bindning i rörelsekapital',
    category: 'Kassaflödesterm',
    format: 'amount',
    hidden: true,
    getValue: (data, i) => {
      const s = sheet(data, 'cashflow', i);
      if (!s) {
        return null;
      }
      const v = num(s.WorkingCaptialCashflow);
      return v === null ? null : -v;
    },
  },

  // --- Nyckeltal (chart definitions where they differ from compass) ---
  {
    baseId: 'kpi-rorelsemarginal',
    name: 'Rörelsemarginal',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      return s ? div(num(s.K), num(s.A)) : null;
    },
  },
  {
    baseId: 'kpi-omsattningstillvaxt',
    name: 'Omsättningstillväxt',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const cur = sheet(data, 'profitloss', i);
      const prev = sheet(data, 'profitloss', i + 1);
      if (!cur || !prev) {
        return null;
      }
      const a0 = num(cur.A);
      const m0 = num(cur.MONTHS) || 12;
      const a1 = num(prev.A);
      const m1 = num(prev.MONTHS) || 12;
      if (a0 === null || a1 === null || a1 === 0) {
        return null;
      }
      return (a0 * (12 / m0)) / (a1 * (12 / m1)) - 1;
    },
  },
  {
    baseId: 'kpi-soliditet',
    name: 'Soliditet',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: solidity,
  },
  {
    baseId: 'kpi-soliditet-heltal',
    name: 'Soliditet',
    category: 'Nyckeltal',
    format: 'percent0',
    hidden: true,
    getValue: solidity,
  },
  {
    baseId: 'kpi-rorelsekapital-pct',
    name: 'Rörelsekapital i procent av omsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const b = sheet(data, 'balance', i);
      const p = sheet(data, 'profitloss', i);
      if (!b || !p) {
        return null;
      }
      const k = num(b.K);
      const q = num(b.Q);
      const a = num(p.A);
      if (k === null || q === null || a === null || a === 0) {
        return null;
      }
      return (k - q) / a;
    },
  },
  {
    baseId: 'kpi-kassalikviditet',
    name: 'Kassalikviditet',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: quickRatio,
  },
  {
    baseId: 'kpi-likviditet-heltal',
    name: 'Likviditet',
    category: 'Nyckeltal',
    format: 'percent0',
    hidden: true,
    getValue: quickRatio,
  },
  {
    baseId: 'kpi-rantetackningsgrad',
    name: 'Räntetäckningsgrad',
    category: 'Nyckeltal',
    format: 'multiple',
    getValue: (data, i) => {
      const sheets = data && data.ranking && data.ranking.sheets;
      if (!sheets || !sheets[i] || !sheets[i].keyValues) {
        return null;
      }
      return num(sheets[i].keyValues.interestCoverage);
    },
  },
  {
    baseId: 'kpi-riskbuffert',
    name: 'Riskbuffert',
    category: 'Nyckeltal',
    format: 'percentPoints',
    getValue: (data, i) => {
      const sheets = data && data.ranking && data.ranking.sheets;
      if (!sheets || !sheets[i] || !sheets[i].keyValues) {
        return null;
      }
      return num(sheets[i].keyValues.riskBuffer);
    },
  },
  {
    baseId: 'kpi-rantabilitet-ek',
    name: 'Räntabilitet på eget kapital',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const p = sheet(data, 'profitloss', i);
      const b = sheet(data, 'balance', i);
      if (!p || !b) {
        return null;
      }
      return div(num(p.N), (num(b.M) || 0) + 0.794 * (num(b.N) || 0));
    },
  },
  {
    baseId: 'kpi-avkastning-totalt',
    name: 'Avkastning på totalt kapital',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const p = sheet(data, 'profitloss', i);
      const b = sheet(data, 'balance', i);
      if (!p || !b) {
        return null;
      }
      return div(num(p.K), num(b.S));
    },
  },
  {
    baseId: 'kpi-bruttomarginal',
    name: 'Bruttomarginal',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      if (!s) {
        return null;
      }
      return div((num(s.A) || 0) + (num(s.D) || 0), num(s.A));
    },
  },
  {
    baseId: 'kpi-foradlingsgrad',
    name: 'Förädlingsgrad',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      if (!s) {
        return null;
      }
      return div((num(s.K) || 0) - (num(s.J) || 0) - (num(s.G) || 0), num(s.A));
    },
  },
  {
    baseId: 'kpi-nettomarginal',
    name: 'Nettomarginal',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      return s ? div(num(s.R), num(s.A)) : null;
    },
  },
  {
    baseId: 'kpi-operativt-cf-pct',
    name: 'Operativa kassaflöden i procent av nettoomsättningen',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const p = sheet(data, 'profitloss', i);
      if (!c || !p) {
        return null;
      }
      return div(num(c.OperatingCashflow), num(p.A));
    },
  },
  {
    baseId: 'kpi-rorelsekapital-bindning-pct',
    name: 'Bindning i rörelsekapital i procent av nettoomsättningen',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const b = sheet(data, 'balance', i);
      const p = sheet(data, 'profitloss', i);
      if (!b || !p) {
        return null;
      }
      const k = num(b.K);
      const j = num(b.J);
      const q = num(b.Q);
      const u = num(b.U);
      if (k === null || j === null || q === null || u === null) {
        return null;
      }
      return div(k - j - q + u, num(p.A));
    },
  },
  {
    baseId: 'kpi-kassa-pct',
    name: 'Kassa och bank i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const b = sheet(data, 'balance', i);
      const p = sheet(data, 'profitloss', i);
      if (!b || !p) {
        return null;
      }
      return div(num(b.J), num(p.A));
    },
  },
  {
    baseId: 'kpi-rantebarande-pct',
    name: 'Räntebärande skulder i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const p = sheet(data, 'profitloss', i);
      if (!p) {
        return null;
      }
      return div(interestBearingLiabilities(data, i), num(p.A));
    },
  },
  {
    baseId: 'kpi-investeringar-pct',
    name: 'Investeringar i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const p = sheet(data, 'profitloss', i);
      if (!c || !p) {
        return null;
      }
      return div(num(c.InvestmentsCashflow), num(p.A));
    },
  },
  {
    baseId: 'kpi-agarkapital-pct',
    name: 'Ägarkapital i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const p = sheet(data, 'profitloss', i);
      if (!c || !p) {
        return null;
      }
      return div(num(c.EquityWithoutNetProfitDelta), num(p.A));
    },
  },
  {
    baseId: 'kpi-utdelning-resultat',
    name: 'Utdelade medel i förhållande till föregående periods resultat',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const pPrev = sheet(data, 'profitloss', i + 1);
      if (!c || !pPrev) {
        return null;
      }
      const eq = num(c.EquityWithoutNetProfitDelta);
      if (eq === null || -eq <= 0) {
        return 0;
      }
      return div(-eq, num(pPrev.R));
    },
  },
  {
    baseId: 'kpi-utdelning-kassa',
    name: 'Utdelade medel i förhållande till föregående periods kassa och bank',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const bPrev = sheet(data, 'balance', i + 1);
      if (!c || !bPrev) {
        return null;
      }
      const eq = num(c.EquityWithoutNetProfitDelta);
      if (eq === null || -eq <= 0) {
        return 0;
      }
      return div(-eq, num(bPrev.J));
    },
  },
  {
    baseId: 'kpi-tillfort-agarkapital',
    name: 'Tillfört ägarkapital i förhållande till utgående kassa och bank',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const b = sheet(data, 'balance', i);
      if (!c || !b) {
        return null;
      }
      const eq = (num(c.EquityWithoutNetProfitDelta) || 0) + (num(c.SubscribedCapitalUnpaidDelta) || 0);
      if (eq <= 0) {
        return 0;
      }
      return div(eq, num(b.J));
    },
  },
  {
    baseId: 'kpi-bruttomarginal-ex-personal',
    name: 'Bruttomarginal efter personalkostnader',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      if (!s) {
        return null;
      }
      return div((num(s.E) || 0) + (num(s.G) || 0), num(s.A));
    },
  },
  {
    baseId: 'kpi-personalkostnader-pct',
    name: 'Personalkostnader i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      return s ? div(num(s.G), num(s.A)) : null;
    },
  },
  {
    baseId: 'kpi-lager-pct',
    name: 'Lager i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const b = sheet(data, 'balance', i);
      const p = sheet(data, 'profitloss', i);
      if (!b || !p) {
        return null;
      }
      return div(num(b.F), num(p.A));
    },
  },
  {
    baseId: 'kpi-ovriga-externa-pct',
    name: 'Övriga externa kostnader i procent av nettoomsättning',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'profitloss', i);
      return s ? div(num(s.F), num(s.A)) : null;
    },
  },
  {
    baseId: 'kpi-fritt-cf-resultat',
    name: 'Fritt kassaflöde i procent av årets resultat',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const c = sheet(data, 'cashflow', i);
      const p = sheet(data, 'profitloss', i);
      if (!c || !p) {
        return null;
      }
      const free = (num(c.CashFlow) || 0) - (num(c.FinancingCashflow) || 0);
      return div(free, num(p.R));
    },
  },
  {
    baseId: 'kpi-omsattning-per-anstalld',
    name: 'Omsättning per anställd',
    category: 'Nyckeltal',
    format: 'amount',
    latestOnly: true,
    getValue: (data, i) => {
      if (i !== 0) {
        return null;
      }
      const s = sheet(data, 'profitloss', 0);
      const emp = employees(data);
      if (!s || emp === null) {
        return null;
      }
      return div(num(s.A), emp);
    },
  },
  {
    baseId: 'kpi-rorelseresultat-per-anstalld',
    name: 'Rörelseresultat per anställd',
    category: 'Nyckeltal',
    format: 'amount',
    latestOnly: true,
    getValue: (data, i) => {
      if (i !== 0) {
        return null;
      }
      const s = sheet(data, 'profitloss', 0);
      const emp = employees(data);
      if (!s || emp === null) {
        return null;
      }
      return div(num(s.K), emp);
    },
  },
  {
    baseId: 'kpi-tillvaxt-rorelseresultat',
    name: 'Tillväxt i rörelseresultat',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const cur = sheet(data, 'profitloss', i);
      const prev = sheet(data, 'profitloss', i + 1);
      if (!cur || !prev) {
        return null;
      }
      return div((num(cur.K) || 0) - (num(prev.K) || 0), num(prev.K));
    },
  },
  {
    baseId: 'kpi-forandring-rorelsemarginal',
    name: 'Förändring i rörelsemarginal',
    category: 'Nyckeltal',
    format: 'percentPoints',
    getValue: (data, i) => {
      const cur = sheet(data, 'profitloss', i);
      const prev = sheet(data, 'profitloss', i + 1);
      if (!cur || !prev) {
        return null;
      }
      const m0 = div(num(cur.K), num(cur.A));
      const m1 = div(num(prev.K), num(prev.A));
      if (m0 === null || m1 === null) {
        return null;
      }
      return (m0 - m1) * 100;
    },
  },
  {
    baseId: 'kpi-forandring-soliditet',
    name: 'Förändring i soliditet',
    category: 'Nyckeltal',
    format: 'percentPoints',
    getValue: (data, i) => {
      const cur = sheet(data, 'balance', i);
      const prev = sheet(data, 'balance', i + 1);
      if (!cur || !prev) {
        return null;
      }
      const s0 = div((num(cur.M) || 0) + 0.794 * (num(cur.N) || 0), num(cur.L));
      const s1 = div((num(prev.M) || 0) + 0.794 * (num(prev.N) || 0), num(prev.L));
      if (s0 === null || s1 === null) {
        return null;
      }
      return (s0 - s1) * 100;
    },
  },
  {
    baseId: 'kpi-balanslikviditet',
    name: 'Balanslikviditet',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'balance', i);
      if (!s) {
        return null;
      }
      return div(num(s.K), num(s.Q));
    },
  },
  {
    baseId: 'kpi-kassa-ratio',
    name: 'Kassa i förhållande till kortfristiga skulder',
    category: 'Nyckeltal',
    format: 'percent',
    getValue: (data, i) => {
      const s = sheet(data, 'balance', i);
      if (!s) {
        return null;
      }
      return div(num(s.J), num(s.Q));
    },
  },

  // --- Bolagsinformation ---
  info('info-bolagsnamn', 'Bolagsnamn', overviewField('NAME')),
  info('info-orgnr', 'Organisationsnummer', (data) => {
    const raw = overviewField('OrgNr')(data);
    return raw ? applyFilter('ORGNR', raw) : null;
  }),
  info('info-registreringsar', 'Registreringsår', incorporationYear),
  info('info-grundat-ar', 'Grundat år', incorporationYear),
  info('info-adress', 'Adress', overviewField('ADDRESS')),
  info('info-postnummer', 'Postnummer', (data) => {
    const raw = overviewField('ZIPCODE')(data);
    return raw ? applyFilter('ZIP', raw) : null;
  }),
  info('info-ort', 'Ort', overviewField('TOWN')),
  info('info-kommun', 'Kommun', overviewField('COMMUNITY')),
  info('info-sni', 'SNI-kod', overviewField('BRANSCH')),
  info('info-bransch', 'Bransch', overviewField('BRANSCH_TEXT')),
  info('info-verksamhet', 'Verksamhetstext', overviewField('ACTIVITY_TEXT')),
  info('info-vd', 'VD', (data) => {
    const raw = overviewField('CEO_NAME')(data);
    return raw ? applyFilter('NAME', raw) : null;
  }),
  info('info-ordforande', 'Ordförande', (data) => {
    const raw = overviewField('CHAIRMAN_NAME')(data);
    return raw ? applyFilter('NAME', raw) : null;
  }),
  info('info-revisor', 'Revisor', (data) => {
    const raw = overviewField('ACCOUNTANT_NAME')(data);
    return raw ? applyFilter('NAME', raw) : null;
  }),
  info('info-anstallda', 'Antal anställda', (data) => employeeLine(data, 'anställd', 'anställda')),
  info('info-employees', 'Employees', (data) => employeeLine(data, 'employee', 'employees')),
  info('info-aktiekapital', 'Aktiekapital', (data) => {
    const raw = overviewField('SHARE_CAPITAL')(data);
    if (!raw) {
      return null;
    }
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) {
      return raw;
    }
    const amount = Number(digits);
    if (!Number.isFinite(amount)) {
      return raw;
    }
    const f = filters();
    if (f.KR) {
      return f.KR(amount);
    }
    return amount.toLocaleString('sv-SE') + ' kr';
  }),
];

function periodId(baseId: string, period: MentionPeriod): string {
  if (period === 'senaste') {
    return baseId;
  }
  if (period === 'foregaende') {
    return baseId + '-prev';
  }
  return baseId + '-change';
}

function buildCatalog(): MentionDef[] {
  const out: MentionDef[] = [];
  for (const base of BASE_MENTIONS) {
    const periods: MentionPeriod[] = base.latestOnly
      ? ['senaste']
      : ['senaste', 'foregaende', 'forandring'];
    for (const period of periods) {
      out.push({
        id: periodId(base.baseId, period),
        baseId: base.baseId,
        name: base.name + PERIOD_LABEL[period],
        category: base.category,
        period,
        format: base.format,
        hidden: base.hidden,
        getValue: base.getValue,
        getText: base.getText,
      });
    }
  }
  return out;
}

export const MENTION_CATALOG: MentionDef[] = buildCatalog();

const CATALOG_BY_ID: Record<string, MentionDef> = {};
for (const m of MENTION_CATALOG) {
  CATALOG_BY_ID[m.id] = m;
}

export function getMentionDef(id: string): MentionDef | undefined {
  return CATALOG_BY_ID[id];
}

export function formatMentionValue(
  value: number | null,
  format: FormatKind,
  period: MentionPeriod,
): string {
  if (value === null || !Number.isFinite(value)) {
    return '–';
  }
  const f = filters();
  if (format === 'amount') {
    if (f.KRP) {
      return f.KRP(value);
    }
    return String(Math.round(value));
  }
  if (format === 'multiple') {
    const g = f.GGR ? f.GGR(value) : String(Math.round(value));
    return g + ' ggr';
  }
  if (format === 'percentPoints') {
    // Already in percentage points (e.g. 1.5 means 1.5 pp)
    const sign = period === 'forandring' && value > 0 ? '+' : '';
    return (
      sign +
      value.toLocaleString('sv-SE', {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      }) +
      ' %'
    );
  }
  // percent as fraction (0.15 -> 15,0 %). percent0 is the same ratio with no decimals (15%).
  if (format === 'percent' || format === 'percent0') {
    if (period === 'forandring') {
      const pp = value * 100;
      const sign = pp > 0 ? '+' : '';
      return (
        sign +
        pp.toLocaleString('sv-SE', {
          maximumFractionDigits: 1,
          minimumFractionDigits: 1,
        }) +
        ' %'
      );
    }
    if (format === 'percent0') {
      const pct = value * 100;
      const body = f.D0 ? f.D0(pct) : String(Math.round(pct));
      return body + '%';
    }
    if (f.P) {
      return f.P(value);
    }
    return (value * 100).toFixed(1) + ' %';
  }
  return String(value);
}

export function resolveMentionRaw(id: string, data: any): number | null {
  const def = CATALOG_BY_ID[id];
  if (!def) {
    return null;
  }
  if (!data) {
    return null;
  }
  if (def.period === 'senaste') {
    return def.getValue(data, 0);
  }
  if (def.period === 'foregaende') {
    return def.getValue(data, 1);
  }
  const cur = def.getValue(data, 0);
  const prev = def.getValue(data, 1);
  if (cur === null || prev === null) {
    return null;
  }
  return cur - prev;
}

export function resolveMention(id: string, data: any): string {
  const def = CATALOG_BY_ID[id];
  if (!def) {
    return '–';
  }
  if (!data) {
    return def.name;
  }
  if (def.format === 'text') {
    const text = def.getText ? def.getText(data) : null;
    if (text === null || text === undefined || text === '') {
      return '–';
    }
    return text;
  }
  const raw = resolveMentionRaw(id, data);
  return formatMentionValue(raw, def.format, def.period);
}

export function mentionDisplayLabel(id: string, data: any | null | undefined): string {
  const def = CATALOG_BY_ID[id];
  if (!def) {
    return '–';
  }
  if (!data) {
    return def.name;
  }
  return resolveMention(id, data);
}

export function filterMentions(query: string, data: any | null | undefined): MentionItem[] {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);

  return MENTION_CATALOG.filter((m) => {
    if (m.hidden) {
      return false;
    }
    if (!terms.length) {
      return true;
    }
    const name = m.name.toLowerCase();
    return terms.every((term) => name.includes(term));
  }).map((m) => ({
    id: m.id,
    name: m.name,
    category: m.category,
    period: m.period,
    formattedValue: data ? resolveMention(m.id, data) : '–',
  }));
}

export type TextSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; id: string };

export function parseMentionText(text: string | null | undefined): TextSegment[] {
  if (!text) {
    return [];
  }
  const segments: TextSegment[] = [];
  let last = 0;
  const re = new RegExp(TOKEN_RE.source, 'g');
  let match: RegExpExecArray | null = re.exec(text);
  while (match !== null) {
    if (match.index > last) {
      segments.push({ type: 'text', value: text.slice(last, match.index) });
    }
    // Unknown ids stay mentions so editing round-trips the token; rendering shows '–'.
    segments.push({ type: 'mention', id: match[1] });
    last = match.index + match[0].length;
    match = re.exec(text);
  }
  if (last < text.length) {
    segments.push({ type: 'text', value: text.slice(last) });
  }
  return segments;
}

export function serializeMentionSegments(segments: TextSegment[]): string {
  return segments
    .map((s) => (s.type === 'mention' ? `{{${s.id}}}` : s.value))
    .join('');
}

/** Visible length counting mention display labels, not tokens. */
export function visibleMentionLength(text: string, data: any | null | undefined): number {
  return parseMentionText(text)
    .map((s) =>
      s.type === 'mention' ? mentionDisplayLabel(s.id, data) : s.value,
    )
    .join('').length;
}

/** Replace tokens with formatted values for slide display. */
export function resolveMentionText(text: string | null | undefined, data: any | null | undefined): string {
  if (!text) {
    return '';
  }
  return text.replace(TOKEN_RE, (match: string, id: string) => {
    if (!CATALOG_BY_ID[id] || !data) {
      return '–';
    }
    return resolveMention(id, data);
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Replace tokens with formatted values for slide/preview v-html.
 * Plain text (same look as surrounding copy); escaped for safe insertion.
 */
export function resolveMentionHtml(
  text: string | null | undefined,
  data: any | null | undefined,
): string {
  if (!text) {
    return '';
  }
  return parseMentionText(text)
    .map((seg) => {
      if (seg.type === 'text') {
        return escapeHtml(seg.value).replace(/\n/g, '<br>');
      }
      const value = data ? resolveMention(seg.id, data) : '–';
      return escapeHtml(value);
    })
    .join('');
}

export function hasMentions(text: string | null | undefined): boolean {
  if (!text) {
    return false;
  }
  TOKEN_RE.lastIndex = 0;
  return TOKEN_RE.test(text);
}
